import { Brand, Wave, WaveMap } from 'common/types/common';
import { Segment } from 'common/types/segments';
import { FilterNames } from 'common/types/filters';
import {
  ChartConfiguration,
  ChartDataPoint,
  ChartFormData,
  RawChart,
  ChartViews,
  ChartType,
  RawChartData,
  RawChartSchema,
  SortOption,
  SortType,
} from 'common/types/chart';
import { getSecondDimension } from 'utils/helpers';
import { sortDataByPrimaryBrands } from 'utils/brands';
import { MARKET_SIZE_KPI } from 'settings/constants';
import { KPI } from 'common/types/kpi';
import { BpChartData, BpColumnKey } from 'modules/charts/components/BrandPerception/types';

export const mapChartPayload = ({ chart, data, branching_data }: RawChart): RawChart => {
  if (!chart?.configuration) {
    return {
      data,
      chart,
      branching_data,
    };
  }

  const filtersNamesMap = {
    wave: chart.wave_ids,
    country: chart.country_ids,
    brand: chart.brand_ids,
    segment: chart.segment_ids,
    kpi: chart.kpi_option_identifiers,
  } as { [key: string]: Array<string | number> };

  const firstDimensionValues = filtersNamesMap[chart.configuration.first_dimension] || [];
  const secondDimensionValues = filtersNamesMap[chart.configuration.second_dimension] || [];

  const sortedData = [...data].sort((dataA, dataB) => {
    const firstDimA = firstDimensionValues.indexOf(dataA[chart.configuration.first_dimension]);
    const firstDimB = firstDimensionValues.indexOf(dataB[chart.configuration.first_dimension]);

    if (firstDimA > firstDimB) return 1;
    if (firstDimA < firstDimB) return -1;

    const secondDimA = secondDimensionValues.indexOf(dataA[chart.configuration.second_dimension]);
    const secondDimB = secondDimensionValues.indexOf(dataB[chart.configuration.second_dimension]);

    if (secondDimA > secondDimB) return 1;
    if (secondDimA < secondDimB) return -1;

    return 0;
  });

  return {
    data: sortedData,
    chart,
    branching_data,
  };
};

interface ChartParams {
  defaultWaves: number[];
  defaultKpis: string[];
  defaultBrands: number[];
  defaultSegments: number[];
}

interface DefaultChartParams {
  wave_ids: number[];
  brand_ids: number[];
  segment_ids: number[];
  country_ids: number[];
  kpi_option_identifiers: string[];
}

export const remapChartParams = ({ defaultWaves, defaultKpis, defaultBrands, defaultSegments }: ChartParams): DefaultChartParams => ({
  wave_ids: defaultWaves,
  brand_ids: defaultBrands,
  segment_ids: defaultSegments,
  country_ids: [],
  kpi_option_identifiers: defaultKpis,
});

export const getMarketSizeDefaultParams = (
  brands: Brand[],
  segments: Segment[],
  kpi: KPI[],
): { defaultBrands: number[]; defaultSegments: number[]; defaultKpis: string[] } => {
  const marketSizeKpi = kpi.find(x => x.kpi_identifier === MARKET_SIZE_KPI)?.kpi_identifier;
  const globalBrandId = brands.find(x => x.global)?.id;
  const genPopSegmentId = segments.find(({ general_population }) => general_population === true)?.id;
  return {
    defaultBrands: globalBrandId ? [globalBrandId] : [],
    defaultSegments: genPopSegmentId ? [genPopSegmentId] : [],
    defaultKpis: marketSizeKpi ? [marketSizeKpi] : [],
  };
};

export const generateChartFormData = ({
  chart: {
    uuid,
    name,
    description,
    chart_type,
    wave_ids,
    brand_ids,
    focus_brand_id,
    segment_ids,
    country_ids,
    kpi_option_identifiers,
    configuration,
    current_view,
    display_reference_average,
    margin_of_error_status,
    dynamic_waves,
    study_uuid,
    owner,
    folders,
    cadence,
  },
}: RawChart): ChartFormData => {
  return {
    uuid,
    name: name as string,
    description: description as string,
    chart_type,
    current_view: current_view || ChartViews.BAR,
    country: country_ids,
    cadence,
    wave: wave_ids,
    kpi: kpi_option_identifiers,
    // Brand Funnel edits only its focus brand; brand_ids holds the backend-derived comparison set.
    brand: chart_type === ChartType.BRAND_FUNNEL ? (focus_brand_id ? [focus_brand_id] : []) : brand_ids,
    segment: segment_ids,
    firstDimension: configuration?.first_dimension,
    secondDimension: getSecondDimension([wave_ids, brand_ids, segment_ids, country_ids, kpi_option_identifiers], configuration),
    chartNumbersType: configuration?.chart_value_representation,
    display_reference_average,
    margin_of_error_status,
    dynamic_waves,
    study_uuid,
    owner,
    folders,
  };
};

export const getAllChartTypes = (): Array<string> => Object.values(ChartType);

export const adjustChartConfig = (chart: RawChartSchema): ChartConfiguration => {
  if (!chart.configuration) return chart.configuration;

  const configuration = { ...chart.configuration };
  configuration.second_dimension = getDimension(chart);

  return configuration;
};

const getDimension = (chart: RawChartSchema) => {
  const firstDim = chart.configuration?.first_dimension;
  const secondDim = chart.configuration?.second_dimension;

  if (firstDim === secondDim) {
    return resolveDuplicateDimensions(firstDim);
  }

  return secondDim;
};

const resolveDuplicateDimensions = (firstDimension: FilterNames) =>
  firstDimension === FilterNames.KPI ? FilterNames.BRAND : FilterNames.KPI;

// adjust waves in the chart in case some wave was deleted
export const adjustWaves = (chart: RawChart, waves: Wave[]): RawChart => {
  const waveIds = waves.map(({ id }) => id);
  chart.chart.wave_ids = chart.chart.wave_ids.filter(waveID => waveIds.includes(waveID));

  return chart;
};

export const roundValue = (value: number, decimalsEnabled: boolean): number => {
  const booleanAsNumber = Number(decimalsEnabled);
  return Number(value?.toFixed(booleanAsNumber));
};

export const displayValue = (value: number, decimalsEnabled: boolean): string => value.toFixed(Number(decimalsEnabled));

export const setCurrentView = (currentView: string | null, chartDimensions: Array<FilterNames | null | undefined>): string => {
  const isDimensionNotWave = !chartDimensions.includes(FilterNames.WAVE);
  return currentView === ChartViews.LINE && isDimensionNotWave ? ChartViews.BAR : currentView || ChartViews.BAR;
};

export const isChartWithConfig = (chartType: ChartType): boolean => {
  return ![ChartType.GAUGE, ChartType.MARKET_SIZE].includes(chartType);
};

// Group data by active "group by" filter
const groupElementsByFilter = (data: RawChartData[], groupBy: FilterNames): RawChartData[] => {
  data.sort((a, b) => {
    if (groupBy === FilterNames.KPI) {
      const valueA = String(a?.[groupBy] || '').toUpperCase();
      const valueB = String(b?.[groupBy] || '').toUpperCase();
      return valueA === valueB ? 0 : valueA > valueB ? 1 : -1;
    }
    return Number(a[groupBy]) - Number(b[groupBy]) || 0;
  });
  return data;
};

// Sort a group of brands as per their availability in "primaryBrands"
const sortGroupDataByBrand = (
  groupedData: RawChartData[],
  filterPack: Record<string | number, string | Date>,
  primaryBrands: string[],
): RawChartData[] => {
  groupedData.sort((a: RawChartData, b: RawChartData) => {
    const brandAName = String(filterPack[a.brand]) || '';
    const brandBName = String(filterPack[b.brand]) || '';

    // Check if brands are part of "primaryBrands"
    return sortDataByPrimaryBrands(
      { id: a.brand.toString(), name: brandAName },
      { id: b.brand.toString(), name: brandBName },
      primaryBrands,
    );
  });
  return groupedData;
};

// waveMap resolves both raw wave ids and cadence entry ids to a date
const compareWavesByDate = (a: RawChartData, b: RawChartData, waveMap: WaveMap): number => {
  const dateA = waveMap[a.wave]?.date;
  const dateB = waveMap[b.wave]?.date;

  if (dateA && dateB) {
    return new Date(dateA).getTime() - new Date(dateB).getTime();
  }

  // Keep unresolved wave ids behind dated waves so known entries still sort chronologically.
  if (dateA) return -1;
  if (dateB) return 1;

  return 0;
};

const sortGroupDataByWave = (groupedData: RawChartData[], waveMap: WaveMap): RawChartData[] => {
  groupedData.sort((a: RawChartData, b: RawChartData) => compareWavesByDate(a, b, waveMap));
  return groupedData;
};

// Sort waves chronologically within each group while preserving the group order
const sortWavesWithinGroups = (data: RawChartData[], groupBy: FilterNames, waveMap: WaveMap): RawChartData[] => {
  const groupOrder = new Map<string | number, number>();
  data.forEach(row => {
    const groupValue = row[groupBy];
    if (!groupOrder.has(groupValue)) groupOrder.set(groupValue, groupOrder.size);
  });

  return [...data].sort((a, b) => {
    const groupDiff = (groupOrder.get(a[groupBy]) ?? 0) - (groupOrder.get(b[groupBy]) ?? 0);
    if (groupDiff !== 0) return groupDiff;

    return compareWavesByDate(a, b, waveMap);
  });
};

export const sortChartData = ({
  data,
  filterPack,
  primaryBrands,
  groupBy,
  secondDimension,
  waveMap,
}: {
  data: RawChartData[];
  filterPack: Record<string | number, string | Date>;
  primaryBrands: string[] | null;
  groupBy: FilterNames;
  secondDimension: FilterNames;
  waveMap: WaveMap;
}): RawChartData[] => {
  let dataChunk: RawChartData[] = [];
  let sortedData: RawChartData[] = [];
  const copiedData = [...data];
  const elementsGroupedByFilter = groupElementsByFilter(copiedData, groupBy);
  if (groupBy === FilterNames.WAVE) {
    sortedData = sortGroupDataByWave(elementsGroupedByFilter, waveMap);
  } else if (secondDimension === FilterNames.BRAND) {
    elementsGroupedByFilter.forEach((element, i) => {
      // Check if there are no more elemets present in the list
      const hasNoMoreElements = i === elementsGroupedByFilter.length - 1;
      // Check if groupping has been changed or not
      const hasGroupChanged = elementsGroupedByFilter[i + 1]?.[groupBy] !== element[groupBy];

      if (hasNoMoreElements || hasGroupChanged) {
        // push last element of the group
        dataChunk.push(element);
        // after sorting the elements present in dataChunk append them in sortedData
        sortedData = [...sortedData, ...sortGroupDataByBrand(dataChunk, filterPack, primaryBrands || [])];
        // clear the temporary array so that we use it again for new group elements
        dataChunk = [];
      } else {
        dataChunk.push(element); // continue pusthing elements of same group in the temporary array
      }
    });
  } else if (groupBy === FilterNames.BRAND) {
    sortedData = sortGroupDataByBrand(elementsGroupedByFilter, filterPack, primaryBrands || []);
  } else {
    sortedData = elementsGroupedByFilter;
  }

  if (groupBy !== FilterNames.WAVE && secondDimension === FilterNames.WAVE) {
    sortedData = sortWavesWithinGroups(sortedData, groupBy, waveMap);
  }

  return sortedData;
};

export const getItemLabel = (dataPoint: ChartDataPoint, key: string): string =>
  ((dataPoint[`${key}_label` as 'value_0_label'] || dataPoint[`${key}_secondDimension` as 'value_0_secondDimension']) as string) ?? '';

// TODO: reuse it in other table charts
export const sortTableChart = (chartData: Array<BpChartData>, sorting: SortOption) => {
  const { key, type } = sorting;
  return chartData.sort((a, b) => {
    const aValue = a[key as BpColumnKey].value as number;
    const bValue = b[key as BpColumnKey].value as number;

    if (!key || type === SortType.DEFAULT) {
      return 0;
    } else if (type === SortType.DESC) {
      return bValue - aValue;
    } else {
      return aValue - bValue;
    }
  });
};
